import { useEffect, useState, useCallback } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ArrowsClockwise, Bug, MagnifyingGlass, X } from "@phosphor-icons/react";

/**
 * Admin Errors Page — surfaces the most recent client-side crashes
 * captured by /api/client-errors. Read-only triage tool for
 * production debugging: no auth-gated endpoint since a broken frontend
 * can't reliably ship a token, so the LIST view is admin-gated at the
 * UI level instead (route is behind ProtectedRoute + is_admin check).
 *
 * Design choices:
 *   - Auto-refresh every 20s so a solicitor watching this page during
 *     a live incident sees new crashes flow in.
 *   - Expandable rows for full stack + component_stack (usually the
 *     goldenrod that pins down which component/render crashed).
 *   - Kind chips colour-coded so "auth.refresh_failed" and
 *     "react.render_error" jump off the page.
 */
export default function AdminErrorsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());

  const fetchErrors = useCallback(async () => {
    try {
      const { data } = await API.get("/client-errors/recent", { params: { limit: 200 } });
      setItems(data.items || []);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load client errors");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchErrors();
    const t = setInterval(fetchErrors, 20_000);
    return () => clearInterval(t);
  }, [fetchErrors]);

  const toggle = (idx) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const filtered = items.filter((it) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    const p = it.payload || {};
    return (
      (p.kind || "").toLowerCase().includes(q) ||
      (p.message || "").toLowerCase().includes(q) ||
      (p.url || "").toLowerCase().includes(q) ||
      (p.user_id || "").toLowerCase().includes(q) ||
      (it.ip || "").toLowerCase().includes(q)
    );
  });

  const kindStyle = (kind) => {
    const k = (kind || "").toLowerCase();
    if (k.startsWith("react")) return { bg: "#fef2f2", fg: "#b91c1c" };
    if (k.startsWith("auth")) return { bg: "#fffbeb", fg: "#b45309" };
    if (k.startsWith("unhandled")) return { bg: "#fdf4ff", fg: "#a21caf" };
    return { bg: "#f1f5f9", fg: "#334155" };
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    try {
      return new Date(ts * 1000).toLocaleString("en-GB", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit",
      });
    } catch {
      return String(ts);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="admin-errors-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ background: "rgba(200,125,58,0.15)", color: "#c87d3a" }}
          >
            <Bug size={22} weight="duotone" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Client-side errors
            </h1>
            <p className="text-sm text-slate-500">
              Every crash caught in the browser lands here. Refreshes every 20s.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={fetchErrors}
          disabled={loading}
          data-testid="admin-errors-refresh"
          className="gap-2"
        >
          <ArrowsClockwise size={16} className={loading ? "animate-spin" : ""} />
          Refresh
        </Button>
      </div>

      <div className="relative mb-4">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
        <Input
          data-testid="admin-errors-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by kind, message, url, user, or ip..."
          className="pl-9 pr-9"
        />
        {filter && (
          <button
            type="button"
            onClick={() => setFilter("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {err && (
        <Card className="p-4 mb-4 border-red-200 bg-red-50" data-testid="admin-errors-error">
          <p className="text-sm text-red-700">{err}</p>
        </Card>
      )}

      {loading && items.length === 0 ? (
        <Card className="p-8 text-center text-slate-500" data-testid="admin-errors-loading">
          Loading…
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center text-slate-500" data-testid="admin-errors-empty">
          {items.length === 0
            ? "No client errors yet — that's a good thing."
            : "No errors match your filter."}
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((it, idx) => {
            const p = it.payload || {};
            const style = kindStyle(p.kind);
            const isOpen = expanded.has(idx);
            return (
              <Card
                key={idx}
                className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => toggle(idx)}
                data-testid={`admin-errors-row-${idx}`}
              >
                <div className="flex items-start gap-3">
                  <Badge
                    className="shrink-0 font-mono text-[11px]"
                    style={{ background: style.bg, color: style.fg, border: "none" }}
                  >
                    {p.kind || "unknown"}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {p.message || "(no message)"}
                    </p>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500 mt-1">
                      <span>{formatTime(it.received_at)}</span>
                      {p.user_id && (
                        <span className="font-mono">user: {p.user_id.slice(0, 12)}…</span>
                      )}
                      {p.url && <span className="truncate max-w-md">{p.url}</span>}
                      <span className="font-mono">{it.ip}</span>
                    </div>
                  </div>
                </div>
                {isOpen && (
                  <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
                    {p.stack && (
                      <div>
                        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                          JavaScript stack
                        </p>
                        <pre className="text-[11px] bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words">
                          {p.stack}
                        </pre>
                      </div>
                    )}
                    {p.component_stack && (
                      <div>
                        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                          React component stack
                        </p>
                        <pre className="text-[11px] bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words">
                          {p.component_stack}
                        </pre>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {p.filename && (
                        <div>
                          <span className="text-slate-400">File: </span>
                          <span className="font-mono">{p.filename}:{p.lineno}:{p.colno}</span>
                        </div>
                      )}
                      {p.original_request_url && (
                        <div>
                          <span className="text-slate-400">Request URL: </span>
                          <span className="font-mono">{p.original_request_url}</span>
                        </div>
                      )}
                      {typeof p.on_public_route !== "undefined" && (
                        <div>
                          <span className="text-slate-400">Public route: </span>
                          <span>{p.on_public_route ? "yes" : "no"}</span>
                        </div>
                      )}
                    </div>
                    {it.user_agent && (
                      <p className="text-[11px] text-slate-400 truncate">{it.user_agent}</p>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
